import { createContext, PropsWithChildren, useContext, useState } from 'react';

import { VerificationErrorEnum } from 'contexts/MatrixDeviceVerificationContext/utils';
import useSecretStorageSecurityCode from 'modules/EndToEndEncryption/hooks/useSecretStorageSecurityCode';
import { SetState } from 'utils/types';

export enum CreateSecurityCodeStepsEnum {
  SETUP_PASSCODE = 'setup-passcode',
  CONFIRM_PASSCODE = 'confirm-passcode',
  SUCCESSFULLY_CREATED_PASSCODE = 'successfully-created-passcode',
}

type CreateSecurityCodeDataContextType = {
  currentStep: CreateSecurityCodeStepsEnum;
  setCurrentStep: SetState<CreateSecurityCodeStepsEnum>;
  pin: string;
  setPin: SetState<string>;
  createSecurityCode: (pin: string) => Promise<void>;
  isCreating: boolean;
  errorKey: VerificationErrorEnum | null;
  clearError: () => void;
  close: () => void;
  onSuccess?: () => void;
  onLogout?: () => void;
  mode: 'create' | 'change';
  showSetupDescription: boolean;
  enforced: boolean;
};

export const CreateSecurityCodeDataContext = createContext<CreateSecurityCodeDataContextType | null>(null);

export const useCreateSecurityCodeDataContext = () => {
  const context = useContext(CreateSecurityCodeDataContext);
  if (!context) {
    throw new Error('useCreateSecurityCodeDataContext must be used within CreateSecurityCodeDataProvider');
  }
  return context;
};

type CreateSecurityCodeDataProviderProps = PropsWithChildren<{
  close: () => void;
  onSuccess?: () => void;
  onLogout?: () => void;
  mode?: 'create' | 'change';
  showSetupDescription?: boolean;
  enforced?: boolean;
}>;

export const CreateSecurityCodeDataProvider = ({
  children,
  close,
  onSuccess,
  onLogout,
  mode = 'create',
  showSetupDescription = false,
  enforced = false,
}: CreateSecurityCodeDataProviderProps) => {
  const [currentStep, setCurrentStep] = useState<CreateSecurityCodeStepsEnum>(
    CreateSecurityCodeStepsEnum.SETUP_PASSCODE
  );
  const [pin, setPin] = useState('');
  const { createSecurityCode, isCreating, errorKey, clearError } = useSecretStorageSecurityCode();

  return (
    <CreateSecurityCodeDataContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        pin,
        setPin,
        createSecurityCode,
        isCreating,
        errorKey,
        clearError,
        close,
        onSuccess,
        onLogout,
        mode,
        showSetupDescription,
        enforced,
      }}
    >
      {children}
    </CreateSecurityCodeDataContext.Provider>
  );
};
